import { z } from "zod";
import { OHIP_SERVICE_LOCATION_INDICATORS, OhipBillingFieldsSchema, OhipProvincialRecordSchema, OhipVendorMessageSchema, } from "./ohip-billing.js";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Work items the agent-api ClinicAid worker leases with service-role RPCs
 * (EXECDEV-65), and the results it reports back. The database validates the
 * same shapes again before it changes a claim.
 */
export const OhipSubmissionJobSchema = z
    .object({
    claimId: IdSchema,
    leaseToken: IdSchema,
    action: z.enum(["create", "update"]),
    attempt: z.number().int().positive(),
    firstAttemptAt: IsoDateTimeSchema,
    invoiceId: IdSchema.nullable(),
    vendorRevision: z.number().int().nullable(),
    serviceDate: z.string().date(),
    fields: OhipBillingFieldsSchema,
    patient: z
        .object({
        id: IdSchema,
        firstName: z.string(),
        lastName: z.string(),
        birthDate: z.string().date(),
        sex: z.enum(["female", "male", "unknown", "other"]),
    })
        .strict(),
    provider: z.object({ id: IdSchema, displayName: z.string() }).strict(),
    settings: z
        .object({
        serviceLocationIndicator: z.enum(OHIP_SERVICE_LOCATION_INDICATORS),
        masterNumber: z.string().regex(/^(?:[0-9]{4})?$/),
        payee: z.enum(["P", "S"]),
        specialtyCode: z.string().regex(/^[0-9]{2}$/),
    })
        .strict(),
})
    .strict();
const VendorMessagesSchema = z.array(OhipVendorMessageSchema).max(20);
export const OhipSubmissionResultSchema = z.discriminatedUnion("outcome", [
    z
        .object({
        outcome: z.literal("sent"),
        invoiceId: IdSchema,
        patientId: IdSchema.optional(),
        providerId: IdSchema.optional(),
        vendorStatus: z.string().max(40).nullable().optional(),
        vendorRevision: z.number().int().nonnegative().nullable().optional(),
        messages: VendorMessagesSchema,
    })
        .strict(),
    // ClinicAid refused the invoice, it was edited inside ClinicAid since our last sync
    // (the new revision is recorded, so approving again replaces it), or it was deleted there.
    z
        .object({
        outcome: z.literal("rejected"),
        messages: VendorMessagesSchema.min(1),
        vendorStatus: z.string().max(40).nullable().optional(),
        vendorRevision: z.number().int().nonnegative().nullable().optional(),
    })
        .strict(),
    // Transient: timeouts, rate limits, 5xx. The database retries with backoff.
    z.object({ outcome: z.literal("retry"), error: z.string().max(300) }).strict(),
]);
export const OHIP_VENDOR_STATUSES = [
    "new",
    "incomplete",
    "accepted",
    "rejected",
    "paidatzero",
    "reversal",
    "underpaid",
    "deleted",
    "sending",
    "pending",
    "held",
    "payment_due",
    "paid",
];
export const OhipStatusSyncLeaseSchema = z
    .object({
    leaseToken: IdSchema,
    items: z
        .array(z
        .object({
        claimId: IdSchema,
        invoiceId: IdSchema,
        vendorStatus: z.string().nullable(),
        vendorRevision: z.number().int().nullable(),
        state: z.string(),
    })
        .strict())
        .min(1)
        .max(50),
})
    .strict();
export const OhipStatusSyncResultSchema = z
    .object({
    claimId: IdSchema,
    // The invoice no longer exists in ClinicAid.
    missing: z.boolean().optional(),
    vendorStatus: z.enum(OHIP_VENDOR_STATUSES).optional(),
    vendorRevision: z.number().int().nonnegative().nullable().optional(),
    paidCents: z.number().int().nullable().optional(),
    paymentDate: z.string().date().nullable().optional(),
    messages: VendorMessagesSchema.optional(),
})
    .strict()
    .refine((result) => result.missing === true || result.vendorStatus !== undefined, {
    message: "A status result needs the ClinicAid status unless the invoice is missing",
});
export const OhipEligibilityJobSchema = z
    .object({
    checkId: IdSchema,
    leaseToken: IdSchema,
    healthNumber: z.string().regex(/^[0-9]{10}$/),
    versionCode: z.string().regex(/^[A-Z]{0,2}$/),
    attempt: z.number().int().positive(),
})
    .strict();
export const OhipEligibilityResultSchema = z.discriminatedUnion("outcome", [
    z
        .object({
        outcome: z.literal("checked"),
        eligible: z.boolean(),
        message: z.string().max(500),
        expiryDate: z.string().date().nullable(),
        provincial: OhipProvincialRecordSchema.nullable(),
    })
        .strict(),
    z.object({ outcome: z.literal("pending") }).strict(),
    z.object({ outcome: z.literal("failed"), message: z.string().max(500) }).strict(),
    z.object({ outcome: z.literal("retry") }).strict(),
]);
//# sourceMappingURL=ohip-clinicaid.js.map